import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Chip,
  Pagination,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Stack,
  CircularProgress
} from '@mui/material';
import { FaSearch as SearchIcon, FaEye, FaEdit } from 'react-icons/fa';
import { TbCategoryPlus } from "react-icons/tb";
import { MdDeleteForever } from "react-icons/md";
import { getAllCombos, deleteCombo, updateCombo } from '../../Webservice/offerAPI';
import { useNavigate } from 'react-router-dom';

const getUserId = () => {
    let userId = null;
    ['user', 'adminUser', 'commonUser', 'sellerUser'].forEach(key => {
        if (userId) return;
        const u = sessionStorage.getItem(key) || localStorage.getItem(key);
        if (u) {
            try {
                const parsed = JSON.parse(u);
                userId = parsed._id || parsed.id || null;
            } catch (e) {}
        }
    });
    return userId || sessionStorage.getItem('userId') || localStorage.getItem('userId') || localStorage.getItem('commonUserId') || '';
};

const PAGE_SIZE = 20;

function ComboOfferList() {
    const [combos, setCombos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
    
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [comboToDelete, setComboToDelete] = useState(null);

    const navigate = useNavigate();

    const fetchCombos = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: PAGE_SIZE,
                ...(searchTerm && { search: searchTerm })
            };
            const response = await getAllCombos(params);
            
            const comboData = response.data || response.combos || [];
            setCombos(Array.isArray(comboData) ? comboData : []);
            
            const metaData = response.meta || { page: 1, totalPages: 1, total: comboData.length };
            setMeta({
                page: metaData.page || 1,
                totalPages: metaData.totalPages || Math.ceil(comboData.length / PAGE_SIZE) || 1,
                total: metaData.total || comboData.length
            });
        } catch (error) {
            console.error('Fetch combos failed:', error);
            setCombos([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCombos(meta.page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta.page, searchTerm]);

    const handlePageChange = (event, newPage) => {
        setMeta((prev) => ({ ...prev, page: newPage }));
    };

    const handleToggleActive = async (combo) => {
        try {
            const newStatus = !combo.isActive;
            setCombos(prev => prev.map(c => c._id === combo._id ? { ...c, isActive: newStatus } : c));
            const userId = getUserId();
            await updateCombo(combo._id, { 
                isActive: newStatus,
                action: 'Update',
                entity: 'Combo Offer',
                createdBy: userId,
                activityLog: { action: 'Update', entity: 'Combo Offer', createdBy: userId }
            });
        } catch (error) {
            console.error('Toggle active failed:', error);
            fetchCombos(meta.page);
        }
    };

    const handleDeleteClick = (combo) => {
        setComboToDelete(combo);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setComboToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (comboToDelete) {
            try {
                await deleteCombo(comboToDelete._id);
                setDeleteDialogOpen(false);
                setComboToDelete(null);
                fetchCombos(meta.page);
            } catch (error) {
                console.error('Delete combo failed:', error);
            }
        }
    };

    const calculateComboStock = (products) => {
        if (!products || products.length === 0) return 0;
        let minStock = Infinity;
        products.forEach(item => {
            const p = item.product_id || item;
            const qtyNeeded = item.quantity || 1;
            const available = p.availableQuantity || 0;
            const possibleCombos = Math.floor(available / qtyNeeded);
            if (possibleCombos < minStock) minStock = possibleCombos;
        });
        return minStock === Infinity ? 0 : minStock;
    };

    return (
        <Box p={2} style={{ background: '#f8fafc', borderRadius: '10px' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h3" component="h1" sx={{
                    color: 'text.primary',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontFamily: 'Mulish, sans-serif',
                }}>
                    Combo Offers Management
                    <Chip
                        label={`${meta.total} ${meta.total === 1 ? 'Combo' : 'Combos'}`}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(255, 209, 2, 0.2)',
                            color: '#b39200',
                            fontFamily: 'Mulish, sans-serif',
                            fontWeight: '800',
                            border: '1px solid rgba(255, 209, 2, 0.5)'
                        }}
                    />
                </Typography>

                <Button
                    variant="contained"
                    startIcon={<TbCategoryPlus />}
                    sx={{
                        fontFamily: 'Mulish, sans-serif',
                        background: 'rgb(255 209 2)',
                        color: 'black',
                        textTransform: 'none',
                        borderRadius: '10px',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        px: 3,
                        boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
                        border: '1px solid black',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            background: 'rgb(255 209 2)',
                            boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                    onClick={() => navigate('/offers/combo/create')}
                >
                    Add Combo
                </Button>
            </Box>

            <Box sx={{
                mb: 3,
                p: 2,
                backgroundColor: '#f8fafc',
                borderRadius: 2,
            }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search Combos..."
                            size="small"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setMeta({ ...meta, page: 1 });
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2 }
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #d3d3d3de' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow sx={{
                            '& th': {
                                backgroundColor: '#f8f9fa',
                                fontWeight: 800,
                                py: 2,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: '#5c636a',
                                fontFamily: 'Mulish, sans-serif',
                                borderBottom: '2px solid #edeff1',
                            }
                        }}>
                            <TableCell>Combo Details</TableCell>
                            <TableCell align="center">Products</TableCell>
                            <TableCell align="center">Stock</TableCell>
                            <TableCell align="center">MRP</TableCell>
                            <TableCell align="center">Selling Price</TableCell>
                            <TableCell align="center">Live Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : combos.length > 0 ? (
                            combos.map((combo) => (
                                <TableRow
                                    key={combo._id}
                                    hover
                                    sx={{ '&:hover': { backgroundColor: '#f9fafb' }, transition: 'background-color 0.2s ease' }}
                                >
                                    <TableCell sx={{ cursor: 'pointer' }} onClick={() => navigate(`/offers/combo/view/${combo._id}`)}>
                                        <Typography fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif', fontSize: '0.95rem', color: '#1a1c1e', '&:hover': { color: '#0d47a1' } }}>
                                            {combo.title}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontFamily: 'Mulish, sans-serif', color: '#6b7280', display: 'inline-block', backgroundColor: '#f3f4f6', px: 1, borderRadius: '4px', mt: 1, fontWeight: 600 }}>
                                            Code: {combo.comboCode || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif', color: '#374151', fontSize: '0.95rem' }}>
                                            {combo.products?.length || 0}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        {(() => {
                                            const stock = calculateComboStock(combo.products);
                                            return (
                                                <Chip 
                                                    label={stock > 0 ? `In Stock (${stock})` : 'Out of Stock'} 
                                                    size="small" 
                                                    sx={{ 
                                                        fontWeight: 800, 
                                                        color: stock > 0 ? '#059669' : '#dc2626', 
                                                        backgroundColor: stock > 0 ? '#d1fae5' : '#fee2e2' 
                                                    }} 
                                                />
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography fontWeight={500} sx={{ fontFamily: 'Mulish, sans-serif', color: '#6b7280', fontSize: '0.9rem', textDecoration: 'line-through' }}>
                                            {(() => {
                                                if (combo.b2c_mrp) return `₹${combo.b2c_mrp}`;
                                                const calcMrp = combo.products?.reduce((acc, p) => acc + ((p.product_id?.b2c_mrp || 0) * (p.quantity || 1)), 0);
                                                return calcMrp ? `₹${calcMrp}` : '-';
                                            })()}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography fontWeight={700} sx={{ fontFamily: 'Mulish, sans-serif', color: '#059669', fontSize: '0.95rem' }}>
                                            {combo.b2c_sellingPrice ? `₹${combo.b2c_sellingPrice}` : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch
                                            size="small"
                                            checked={!!combo.isActive}
                                            onChange={() => handleToggleActive(combo)}
                                            color="primary"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Tooltip title="View Combo">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#2196f3', backgroundColor: 'rgba(33, 150, 243, 0.1)', '&:hover': { backgroundColor: 'rgba(33, 150, 243, 0.2)' }, borderRadius: '8px' }}
                                                    onClick={() => navigate(`/offers/combo/view/${combo._id}`)}
                                                >
                                                    <FaEye />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Combo">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#4682B4', backgroundColor: 'rgba(70, 130, 180, 0.1)', '&:hover': { backgroundColor: 'rgba(70, 130, 180, 0.2)' }, borderRadius: '8px' }}
                                                    onClick={() => navigate(`/offers/combo/edit/${combo._id}`)}
                                                >
                                                    <FaEdit />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    sx={{ color: '#dc2626', backgroundColor: 'rgba(220, 38, 38, 0.1)', '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.2)' }, borderRadius: '8px' }}
                                                    onClick={() => handleDeleteClick(combo)}
                                                >
                                                    <MdDeleteForever size={20} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={8} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                                    <Typography variant="body1">No Combos available</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, '& .MuiPagination-ul': { flexWrap: 'nowrap' } }}>
                <Pagination
                    count={meta.totalPages}
                    page={meta.page}
                    onChange={handlePageChange}
                    sx={{
                        '& .MuiPaginationItem-root': {
                            fontFamily: 'Mulish, sans-serif',
                            fontWeight: 600,
                            borderRadius: '8px',
                            '&.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black', '&:hover': { backgroundColor: 'rgb(255, 190, 0)' } }
                        }
                    }}
                    shape="rounded"
                    showFirstButton
                    showLastButton
                />
            </Box>

            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                PaperProps={{
                    sx: { borderRadius: '12px', padding: '8px', minWidth: '400px' }
                }}
            >
                <DialogTitle sx={{ fontFamily: 'Mulish, sans-serif', fontWeight: 800, color: '#1a1c1e', pb: 1 }}>
                    Delete Combo
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontFamily: 'Mulish, sans-serif', color: '#4b5563', fontSize: '0.95rem' }}>
                        Are you sure you want to delete the Combo <strong>{comboToDelete?.title}</strong>? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ padding: '16px' }}>
                    <Button onClick={handleCloseDeleteDialog} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, color: '#4b5563', borderColor: '#d1d5db' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} variant="contained" sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, backgroundColor: '#dc2626', color: 'white', '&:hover': { backgroundColor: '#b91c1c' } }}>
                        Delete Combo
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default ComboOfferList;
